export interface RuntimeConfig {
  /** Base URL of the backend API. Falls back to the build-time VITE_API_URL. */
  apiUrl: string
}

let config: RuntimeConfig | null = null

/**
 * Fetches runtime configuration from `config.json` served next to the app.
 *
 * This decouples the API URL from the build: a single image can target
 * different environments by editing `config.json` (served by nginx) without a
 * rebuild. Any field left empty falls back to the build-time `VITE_*` value so
 * local development keeps working from `.env.local`.
 *
 * Must resolve before the rest of the app is imported — see `main.ts`.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  let fetched: Partial<RuntimeConfig> = {}

  try {
    const response = await fetch(`${import.meta.env.BASE_URL}config.json`, {
      cache: 'no-store',
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    fetched = await response.json()
  } catch (error) {
    console.error('Failed to load config.json, falling back to build-time config', error)
  }

  config = {
    apiUrl: fetched.apiUrl || import.meta.env.VITE_API_URL,
  }

  return config
}

/**
 * Returns the loaded runtime config. Throws if called before
 * `loadRuntimeConfig()` has resolved.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!config) {
    throw new Error('Runtime config accessed before loadRuntimeConfig() resolved')
  }
  return config
}
